import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Star, Plus, Trash2, Loader2, TrendingUp, Award, X, MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const CRITERIA = [
  { key: 'qualite',       label: 'Qualité des travaux',   color: 'from-blue-500 to-blue-700' },
  { key: 'delais',        label: 'Respect des délais',    color: 'from-emerald-500 to-emerald-700' },
  { key: 'tenue',         label: 'Tenue professionnelle', color: 'from-purple-500 to-purple-700' },
  { key: 'disponibilite', label: 'Disponibilité',         color: 'from-orange-500 to-orange-700' },
];

const scoreColor = (n) => {
  if (n == null) return 'text-slate-400';
  if (n >= 8) return 'text-emerald-600';
  if (n >= 6) return 'text-blue-600';
  if (n >= 4) return 'text-orange-600';
  return 'text-red-600';
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
};

/**
 * Bloc de notation interne d'un sous-traitant.
 * - Note générale (moyenne des moyennes)
 * - 4 moyennes par critère (Qualité, Délais, Tenue, Disponibilité)
 * - Historique des évaluations (par chantier)
 * - Ajout via dialog : sélection chantier + 4 sliders 0-10 + commentaire
 */
export default function EvaluationsPanel({ sousTraitantId, allChantiers, onSaved }) {
  const [data, setData] = useState({ evaluations: [], summary: { count: 0, note_generale: null, moyennes: {} } });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    chantier_id: '', qualite: 8, delais: 8, tenue: 8, disponibilite: 8, commentaire: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sous-traitants/${sousTraitantId}/evaluations`);
      setData(r.data);
    } catch (_e) { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sousTraitantId]);

  const resetForm = () => setForm({
    chantier_id: '', qualite: 8, delais: 8, tenue: 8, disponibilite: 8, commentaire: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.chantier_id) { toast.error('Sélectionnez un chantier'); return; }
    setSaving(true);
    try {
      await api.post(`/sous-traitants/${sousTraitantId}/evaluations`, form);
      toast.success('Évaluation enregistrée');
      setAddOpen(false);
      resetForm();
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const handleDelete = async (eid) => {
    if (!window.confirm('Supprimer cette évaluation ?')) return;
    try {
      await api.delete(`/sous-traitants/${sousTraitantId}/evaluations/${eid}`);
      toast.success('Évaluation supprimée');
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  // Aplatis la liste de tous les chantiers dans un seul array pour le dropdown
  const chantiersFlat = [
    ...(allChantiers?.en_cours || []),
    ...(allChantiers?.termines || []),
    ...(allChantiers?.prevus || []),
  ];
  const ng = data.summary?.note_generale;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200" data-testid="evaluations-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Notation interne
          </h2>
          {data.summary?.count > 0 && (
            <span className="text-xs text-slate-500">
              · {data.summary.count} évaluation{data.summary.count > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => setAddOpen(true)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="eval-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Noter
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
      ) : data.summary?.count === 0 ? (
        <div className="text-center py-6 text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">
          Aucune évaluation. Cliquez sur « Noter » après un chantier terminé.
        </div>
      ) : (
        <>
          {/* Note générale */}
          <div className="flex items-center gap-6 mb-5 pb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className={`text-5xl font-bold ${scoreColor(ng)} tabular-nums`}>
                {ng?.toFixed(1) ?? '—'}
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Note générale</div>
                <div className="text-sm text-slate-600">/ 10</div>
                <div className="flex mt-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i}
                      className={`w-3.5 h-3.5 ${i < Math.floor((ng || 0) / 2) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <TrendingUp className="w-8 h-8 text-slate-200 ml-auto" />
          </div>

          {/* Moyennes par critère (bars) */}
          <div className="space-y-2.5 mb-5">
            {CRITERIA.map((c) => {
              const v = data.summary?.moyennes?.[c.key];
              const pct = v != null ? (v / 10) * 100 : 0;
              return (
                <div key={c.key} data-testid={`eval-avg-${c.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-700">{c.label}</span>
                    <span className={`text-sm font-bold tabular-nums ${scoreColor(v)}`}>
                      {v != null ? `${v.toFixed(1)} / 10` : '—'}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${c.color} rounded-full transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Historique */}
          <div className="pt-4 border-t border-slate-100">
            <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">
              Historique
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data.evaluations.map((ev) => (
                <div key={ev.evaluation_id} className="flex items-start gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-testid={`eval-row-${ev.evaluation_id}`}>
                  <div className={`text-xl font-bold ${scoreColor(ev.note_globale)} tabular-nums w-12 text-center shrink-0`}>
                    {ev.note_globale?.toFixed(1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {ev.chantier_reference || ev.chantier_adresse || ev.chantier_id}
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-2 mt-0.5">
                      {CRITERIA.map((c) => (
                        <span key={c.key}>
                          {c.label.split(' ')[0]} <b className={scoreColor(ev.notes?.[c.key])}>{ev.notes?.[c.key]}</b>
                        </span>
                      ))}
                    </div>
                    {ev.commentaire && (
                      <div className="text-xs text-slate-600 mt-1 italic flex items-start gap-1">
                        <MessageSquare className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                        {ev.commentaire}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {fmtDate(ev.created_at)} · {ev.evaluated_by}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(ev.evaluation_id)}
                    className="p-1 text-slate-400 hover:text-red-600 rounded"
                    data-testid={`eval-delete-${ev.evaluation_id}`}
                    title="Supprimer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle évaluation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Chantier concerné</label>
              <Select value={form.chantier_id} onValueChange={(v) => setForm((f) => ({ ...f, chantier_id: v }))}>
                <SelectTrigger data-testid="eval-chantier-select">
                  <SelectValue placeholder="Sélectionner un chantier…" />
                </SelectTrigger>
                <SelectContent>
                  {chantiersFlat.length === 0 && (
                    <div className="px-2 py-2 text-sm text-slate-400">Aucun chantier affecté</div>
                  )}
                  {chantiersFlat.map((c) => (
                    <SelectItem key={c.chantier_id} value={c.chantier_id}>
                      {c.reference || c.adresse || c.chantier_id}
                      {c.statut && <span className="text-slate-400 ml-2">· {c.statut}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {CRITERIA.map((c) => (
              <div key={c.key} data-testid={`eval-input-${c.key}`}>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-slate-700">{c.label}</label>
                  <span className={`text-lg font-bold ${scoreColor(form[c.key])}`}>
                    {form[c.key]} / 10
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={form[c.key]}
                  onChange={(e) => setForm((f) => ({ ...f, [c.key]: parseInt(e.target.value, 10) }))}
                  className="w-full accent-blue-600"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                  <span>0</span><span>5</span><span>10</span>
                </div>
              </div>
            ))}

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Commentaire (optionnel)</label>
              <Textarea
                value={form.commentaire}
                onChange={(e) => setForm((f) => ({ ...f, commentaire: e.target.value }))}
                placeholder="Points forts, points à améliorer, remarques…"
                rows={3}
                data-testid="eval-comment-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} disabled={saving}>
                <X className="w-4 h-4 mr-1" /> Annuler
              </Button>
              <Button type="submit" disabled={saving} data-testid="eval-submit-btn">
                {saving
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />…</>
                  : <><Award className="w-4 h-4 mr-1" />Enregistrer la note</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
